import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import {
  type APIResponse,
  type ReportsMatterSettlementReport,
} from "@/lib/types";
import type { ReportDateRangeType } from "@/lib/utils/reports/report-filter-options";

const MAX_SEARCH_PAGES = 50;
const SETTLEMENT_REPORT_PAGE_LIMIT = 250;

type MatterSettlementReportDateRange = {
  endDate?: string;
  preset: ReportDateRangeType;
  startDate?: string;
};

type MatterSettlementReportFilters = {
  accepted?: boolean[];
  assigneeUserIds?: string[];
  itemTypes?: Array<"COUNTER" | "DEMAND" | "OFFER">;
  matterStatusIds?: string[];
  incidentTypeIds?: string[];
  claimTypeIds?: string[];
  teamIds?: string[];
};

export type RetrieveReportsMatterSettlementReportInput = {
  dateRange: MatterSettlementReportDateRange;
  filters: MatterSettlementReportFilters;
};

type MatterSettlementReportV2Row = {
  accepted?: boolean | null;
  acceptedDate?: string | null;
  amount?: string | number | null;
  assigneeUserIds?: string[];
  attorneyFeeAmount?: string | number | null;
  dateExpires?: string | null;
  depositTrustCheckTotal?: string | number | null;
  eventDate?: string | null;
  id: string;
  insuranceId?: string | null;
  insuranceType?: string | null;
  itemType: "COUNTER" | "DEMAND" | "OFFER";
  matterId: string;
  matterStatusLabel?: string | null;
  matterTitle?: string | null;
  incidentTypeLabel?: string | null;
  claimTypes?: Array<{ id: string; label: string }>;
  originalMedicalBillTotal?: string | number | null;
  policyLimit?: boolean | null;
  policyLimitPerIncident?: string | number | null;
  policyLimitPerPerson?: string | number | null;
  status?: string | null;
  teams?: Array<{ id: string; label: string }>;
};

type MatterSettlementReportV2Response = {
  metrics?: Record<string, string | number | null>;
  rows?: MatterSettlementReportV2Row[];
};

const toNumber = (value: string | number | null | undefined) => {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const toLegacySettlementReport = (
  response: MatterSettlementReportV2Response,
): ReportsMatterSettlementReport => {
  const rows = response.rows ?? [];
  const commonFields = (row: MatterSettlementReportV2Row) => ({
    matterSettlementReportInsuranceId: row.insuranceId ?? undefined,
    matterSettlementReportInsuranceLimitPerIncident: toNumber(
      row.policyLimitPerIncident,
    ),
    matterSettlementReportInsuranceLimitPerPerson: toNumber(
      row.policyLimitPerPerson,
    ),
    matterSettlementReportInsuranceType: row.insuranceType ?? undefined,
    matterSettlementReportMatterAssigneeUserIds: row.assigneeUserIds,
    matterSettlementReportMatterId: row.matterId,
    matterSettlementReportMatterStatusLabel: row.matterStatusLabel ?? undefined,
    matterSettlementReportMatterTeams: row.teams?.map((team) => team.label),
    matterSettlementReportMatterTitle: row.matterTitle ?? undefined,
    claimTypes: row.claimTypes ?? [],
    matterSettlementReportIncidentType: row.incidentTypeLabel ?? undefined,
    matterSettlementReportMedicalBillTotal: toNumber(
      row.originalMedicalBillTotal,
    ),
  });

  const matterSettlementDemands = rows
    .filter((row) => row.itemType === "DEMAND")
    .map((row) => ({
      ...commonFields(row),
      matterSettlementReportDemandAmount: toNumber(row.amount),
      matterSettlementReportDemandAttorneyFeeAmount: toNumber(
        row.attorneyFeeAmount,
      ),
      matterSettlementReportDemandDateExpires: row.dateExpires ?? undefined,
      matterSettlementReportDemandDateSent: row.eventDate ?? undefined,
      matterSettlementReportDemandId: row.id,
      matterSettlementReportDemandPolicyLimit: row.policyLimit ?? undefined,
    }));
  const matterSettlementOffers = rows
    .filter((row) => row.itemType === "OFFER")
    .map((row) => ({
      ...commonFields(row),
      matterSettlementReportOfferAccepted: row.accepted ?? undefined,
      matterSettlementReportOfferAcceptedDate: row.acceptedDate ?? undefined,
      matterSettlementReportOfferAmount: toNumber(row.amount),
      matterSettlementReportOfferAttorneyFeeAmount: toNumber(
        row.attorneyFeeAmount,
      ),
      matterSettlementReportOfferDateReceived: row.eventDate ?? undefined,
      matterSettlementReportOfferId: row.id,
      matterSettlementReportOfferPolicyLimit: row.policyLimit ?? undefined,
      matterSettlementReportOfferStatus: row.status ?? undefined,
    }));
  const matterSettlementCounters = rows
    .filter((row) => row.itemType === "COUNTER")
    .map((row) => ({
      ...commonFields(row),
      matterSettlementReportCounterAccepted: row.accepted ?? undefined,
      matterSettlementReportCounterAcceptedDate: row.acceptedDate ?? undefined,
      matterSettlementReportCounterAmount: toNumber(row.amount),
      matterSettlementReportCounterAttorneyFeeAmount: toNumber(
        row.attorneyFeeAmount,
      ),
      matterSettlementReportCounterDateSent: row.eventDate ?? undefined,
      matterSettlementReportCounterId: row.id,
      matterSettlementReportCounterPolicyLimit: row.policyLimit ?? undefined,
      matterSettlementReportCounterStatus: row.status ?? undefined,
    }));
  const matterTrustChecks = Array.from(
    new Map(
      rows
        .filter((row) => toNumber(row.depositTrustCheckTotal) !== undefined)
        .map((row) => [
          row.matterId,
          {
            matterSettlementReportMatterId: row.matterId,
            matterSettlementReportTrustCheckAmount: toNumber(
              row.depositTrustCheckTotal,
            ),
            matterSettlementReportTrustCheckType: "Deposit",
          },
        ]),
    ).values(),
  );

  return {
    matterSettlementCounters,
    matterSettlementDemands,
    matterSettlementOffers,
    matterTrustChecks,
    metrics: Object.fromEntries(
      Object.entries(response.metrics ?? {}).map(([key, value]) => [
        key,
        toNumber(value),
      ]),
    ),
  };
};

export const retrieveReportsMatterSettlementReport = async (
  data: RetrieveReportsMatterSettlementReportInput,
) => {
  let cursor: string | undefined;
  let pageCount = 0;
  let latestMeta: APIResponse<ReportsMatterSettlementReport>["meta"] = {
    userMsg: "",
  };
  let metrics: ReportsMatterSettlementReport["metrics"];
  const matterSettlementCounters: NonNullable<
    ReportsMatterSettlementReport["matterSettlementCounters"]
  > = [];
  const matterSettlementDemands: NonNullable<
    ReportsMatterSettlementReport["matterSettlementDemands"]
  > = [];
  const matterSettlementOffers: NonNullable<
    ReportsMatterSettlementReport["matterSettlementOffers"]
  > = [];
  const matterTrustChecks: NonNullable<
    ReportsMatterSettlementReport["matterTrustChecks"]
  > = [];

  do {
    const response = await authenticatedFastApiAxiosInstance.post<
      APIResponse<MatterSettlementReportV2Response>
    >("reports/v2/matter-settlements/query", data, {
      params: {
        limit: SETTLEMENT_REPORT_PAGE_LIMIT,
        ...(cursor ? { cursor } : {}),
      },
    });
    const page = response.data;
    latestMeta = page.meta;
    const report = toLegacySettlementReport(page.data);
    metrics ??= report.metrics;
    matterSettlementCounters.push(...(report.matterSettlementCounters ?? []));
    matterSettlementDemands.push(...(report.matterSettlementDemands ?? []));
    matterSettlementOffers.push(...(report.matterSettlementOffers ?? []));
    matterTrustChecks.push(...(report.matterTrustChecks ?? []));
    cursor =
      (page.pagination as { nextCursor?: string | null } | undefined)
        ?.nextCursor ?? undefined;
    pageCount += 1;
  } while (cursor && pageCount < MAX_SEARCH_PAGES);

  return {
    data: {
      matterSettlementCounters,
      matterSettlementDemands,
      matterSettlementOffers,
      matterTrustChecks,
      metrics,
    },
    meta: latestMeta,
  } satisfies APIResponse<ReportsMatterSettlementReport>;
};
